import { mapState, mapGetters, mapActions } from 'vuex';
import { cloneDeep, get, isEqual } from 'lodash';

import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { PAGINATION_DEFAULT, SEARCH_PAGE_LIMIT } from '@/constants/pagination';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

import actionItem from '../Partials/ActionItem/ActionItem.vue';
import buildingKitAiTesting from '../Partials/BuildingKitAiTesting/BuildingKitAiTesting.vue';
import MiddlePaneHeader from '../Partials/IQToolMiddlePaneHeader/IQToolMiddlePaneHeader.vue';
import ActionContainer from '../Partials/ActionContainer/ActionContainer.vue';
import aiqCommandBuilderDialog from '../Partials/BuildingKitAiTesting/BuildingKit/Commands/AIQCommandBuilder/AIQCommandBuilderDialog.vue';
import {
  iqtoolsErrorHandler,
  removeActionIfExist,
  removeDuplicateNameAction,
  makeDependencyAlertMsg,
  confirmDiscardUnsavedChanges,
  Events,
} from '@/libs';
import { STATUS_CODES } from '@/constants';
import * as log from 'loglevel';
import IqToolsDiagram from '../Diagrams/IqToolsDiagram.vue';
import DiagramViewMixin from '../Diagrams/DiagramViewMixin';
import { getWorkflowDiagramInfo } from '../Diagrams/DiagramHelper';
import {
  IQ_TOOL_ACTION_TYPE_DIALOG,
  IQ_TOOL_ACTION_TYPE_WORKFLOW,
} from '@/constants/iqtool';

export default {
  name: 'workflows',
  mixins: [DiagramViewMixin],
  components: {
    actionItem,
    buildingKitAiTesting,
    MiddlePaneHeader,
    ActionContainer,
    ManagementPage,
    IqToolsDiagram,
    aiqCommandBuilderDialog,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    const workflowNameRegex = helpers.regex(/^[a-zA-Z_][a-zA-Z.0-9\s_]*$/);
    return {
      newWorkflowName: { required, workflowNameRegex },
    };
  },
  data() {
    return {
      search: '',
      searchPageSize: SEARCH_PAGE_LIMIT,
      loading: true,
      pagination: PAGINATION_DEFAULT,
      showAddWorkflowDialog: false,
      formWorkflow: null,
      newWorkflowName: '',
      // Diagram state
      nodes: [],
      edges: [],
      containers: [],
      commands: [],
      expandedNodeIds: [],
      diagramKey: 0,
      // Command editor state
      showEditCommandDialog: false,
      selectedCommand: {},
      kitLoaded: false,
      editingFlowNodeId: null,
    };
  },
  computed: {
    ...mapState({
      workflows: (state) => state.workflows.workflows,
      selectedWorkflow: (state) => state.workflows.selected,
      useConditionalAction:
        (state) => state.featureFlags.FEATURE_FLAGS.SUPPORT_CONDITIONAL_ACTION_IN_WORKFLOW,
    }),
    ...mapGetters({
      preconfiguredEntitiesFunc: 'entities/preconfiguredEntities',
      routingProps: 'routing/props',
    }),
    pristine() {
      return isEqual(this.formWorkflow, this.selectedWorkflow);
    },
    middleColumnsClassObj() {
      return { pristine: this.pristine };
    },
    preconfiguredEntities() {
      return (this.preconfiguredEntitiesFunc() || []).map((e) => e.name);
    },
    isSubmittable() {
      return !this.v$.$invalid;
    },
    isEditMode() {
      return !!(this.selectedWorkflow && this.formWorkflow);
    },
    isViewMode() {
      return !!(this.selectedWorkflow && !this.formWorkflow);
    },
    isListMode() {
      return !this.selectedWorkflow;
    },
    isChanged() {
      return !this.pristine && this.isEditMode;
    },
  },
  async beforeRouteLeave(to, from, next) {
    if (this.isEditMode && this.isChanged) {
      const confirmedDiscard = await confirmDiscardUnsavedChanges(this);
      return next(confirmedDiscard);
    }

    return next();
  },
  async mounted() {
    try {
      await Promise.all([
        this.$store.dispatch('entities/getEntitiesList'),
        this.$store.dispatch('dialogs/getEntityOperators'),
      ]);
    } catch (err) {
      this.$aiq.notify.error(this.$t('iq_tools_tab.workflows.unable_to_load'));
      log.error(err);
    }

    Events.on('navigate.same.route', this.handleReloadPage.bind(this));
    await this.onRouteChanged(this.$route);
  },
  beforeUnmount() {
    Events.off('navigate.same.route', this.handleReloadPage.bind(this));
    return this.$store.dispatch('workflows/resetWorkflows');
  },
  watch: {
    async $route(to) {
      await this.onRouteChanged(to);
    },
  },
  methods: {
    ...mapActions({
      getFunctions: 'buildingKit/getFunctions',
      getDBKAll: 'buildingKit/getDBKAll',
    }),

    loadList({ searchTerm, searchOffset }) {
      this.loading = true;
      const params = { q: searchTerm, limit: this.searchPageSize, offset: searchOffset };

      return this.$store.dispatch('workflows/getWorkflowsList', [params, params.offset === 0])
        .then((response) => {
          this.loading = false;
          this.pagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
        });
    },

    openAddWorkflowDialog() {
      this.showAddWorkflowDialog = true;
    },

    closeAddWorkflowDialog() {
      this.showAddWorkflowDialog = false;
      this.newWorkflowName = '';
    },

    addNewWorkflow() {
      if (!this.newWorkflowName) return;

      return this.$store.dispatch('workflows/createWorkflow', { name: this.newWorkflowName })
        .then((data) => {
          this.$aiq.notify.success(this.$t('iq_tools_tab.workflows.workflow_added'));
          this.newWorkflowName = '';
          this.closeAddWorkflowDialog();
          return this.editWorkflow(data.data.id, true);
        }, (err) => {
          iqtoolsErrorHandler(this, err, 'workflow');
        });
    },

    async handleReloadPage(pathInfo) {
      if (pathInfo.fullPath === this.$route.fullPath && this.selectedWorkflow) {
        return await this.goToListPage();
      }
    },

    async goToListPage() {
      if (this.isChanged) {
        const confirmedDiscard = await confirmDiscardUnsavedChanges(this);
        if (!confirmedDiscard) return;
      }
      return this.$store.dispatch('workflows/resetSelected');
    },

    async onRouteChanged(to) {
      const storedWorkflowId = get(this.routingProps, 'workflowId', null);
      if (storedWorkflowId) {
        this.$store.dispatch('routing/setProps', { ...this.routingProps, workflowId: null });
        return await this.editWorkflow(storedWorkflowId, true);
      }

      const workflowName = get(to, 'hash', '').replace('#', '').trim();
      if (workflowName) {
        return await this.editWorkflowByName(workflowName);
      }
    },

    async editWorkflow(id, disableSaveWarning) {
      const dispatchSelectWorkflow = () => {
        if (this.selectedWorkflow && this.selectedWorkflow.id === id) {
          return Promise.resolve(this.formWorkflow = cloneDeep(this.selectedWorkflow));
        }

        return this.$store.dispatch('workflows/selectWorkflow', id)
          .then((data) => { this.formWorkflow = cloneDeep(data.data) })
          .catch(() => {
            this.$aiq.notify.error(this.$t('iq_tools_tab.workflows.not_found'));
            return this.goToListPage();
          });
      };

      if (this.isChanged && this.selectedWorkflow && !disableSaveWarning) {
        const confirmedDiscard = await confirmDiscardUnsavedChanges(this);
        if (!confirmedDiscard) return;
      }
      return dispatchSelectWorkflow();
    },

    editWorkflowByName(workflowName) {
      return this.$store.dispatch('workflows/getWorkflowsList', [
        { q: workflowName, offset: 0, limit: 1 },
        true,
      ]).then((response) => {
        const results = response.data?.data;
        if (response?.data?.pagination?.rowCount === 1 && results?.length > 0) {
          return this.editWorkflow(results[0].id, true);
        }
      });
    },

    onEditCancel() {
      this.formWorkflow = cloneDeep(this.selectedWorkflow);
    },

    onEditWorkflow() {
      return this.editWorkflow(this.selectedWorkflow.id, true);
    },


    async viewWorkflow(id = null) {
      if (id === null && this.selectedWorkflow) {
        id = this.selectedWorkflow.id;
      }
      if (id === null) {
        return this.goToListPage();
      }

      const initializeDiagram = () => {
        this.formWorkflow = null;
        this.expandedNodeIds = [];
        if (this.selectedWorkflow) {
          const { nodes, edges, commands, containers } = getWorkflowDiagramInfo(this.selectedWorkflow);
          this.nodes = nodes;
          this.edges = edges;
          this.containers = containers || [];
          this.commands = commands;
        }
      };

      const goToViewPage = () => {
        if (this.selectedWorkflow && id === this.selectedWorkflow.id) {
          return Promise.resolve(initializeDiagram());
        }
        this.nodes = [];
        this.edges = [];
        this.containers = [];
        return this.$store.dispatch('workflows/selectWorkflow', id)
          .then(() => initializeDiagram())
          .catch(() => {
            this.$aiq.notify.error(this.$t('iq_tools_tab.workflows.not_found'));
            return this.goToListPage();
          });
      };

      if (this.isChanged) {
        const confirmedDiscard = await confirmDiscardUnsavedChanges(this);
        if (!confirmedDiscard) return;
      }
      return goToViewPage();
    },

    onCopyWorkflow() {
      return this.$store.dispatch('workflows/copyWorkflow', this.selectedWorkflow.id)
        .then((data) => {
          this.$aiq.notify.success(this.$t('iq_tools_tab.workflows.copied'));
          return this.editWorkflow(data.id, true);
        }, (err) => {
          iqtoolsErrorHandler(this, err, 'workflow');
        });
    },

    deleteWorkflow(id) {
      const { name } = this.workflows.find((e) => e.id === id);
      this.$aiq.confirm(
        this.$t('iq_tools_tab.workflows.delete.title'),
        this.$t('iq_tools_tab.workflows.delete.confirm', { name }),
      ).then(() => {
        return this.$store.dispatch('workflows/deleteWorkflow', id)
          .then(() => {
            this.$aiq.notify.success(this.$t('iq_tools_tab.workflows.delete.workflow_deleted'));
            this.formWorkflow = null;
            return this.goToListPage();
          })
          .catch((res) => {
            const { title, content } = makeDependencyAlertMsg('Workflow', res, { $t: this.$t });
            this.$aiq.confirm(title, content);
          });
      });
    },

    saveFullWorkflow() {
      return this._updateWorkflow()
        .then((newWorkflow) => {
          this.$aiq.notify.success(this.$t('iq_tools_tab.workflows.save.success'));
          return this.editWorkflow(newWorkflow.id, true);
        })
        .catch((res) => {
          if (res.response.status === STATUS_CODES.CONFLICT) {
            const messagePrefix = 'To finish this workflow, customer\'s ';
            const messageSuffix = 'needed. Do you want to proceed it anyway?';

            const missingEntities = get(res.response, 'data.missing', [])
              .map((entity) => `<span style="color:#5993FF">${entity}</span>`);

            let message;
            if (missingEntities.length === 1) {
              message = `${messagePrefix}${missingEntities[0]} is ${messageSuffix}`;
            } else {
              const last = missingEntities[missingEntities.length - 1];
              const rest = missingEntities.slice(0, -1).join(', ');
              message = `${messagePrefix}${rest} and ${last} are ${messageSuffix}`;
            }

            this.$aiq.confirm('Missing entities', message)
              .then(() => this._updateWorkflow(true).then((newWorkflow) => {
                this.$aiq.notify.success(this.$t('iq_tools_tab.workflows.save.success'));
                return this.editWorkflow(newWorkflow.id, true);
              }))
              .catch(() => this.$aiq.notify.error(this.$t('iq_tools_tab.workflows.save.unable_to_save')));
          }
        });
    },

    _updateWorkflow(force = false) {
      return this.$store.dispatch('workflows/updateWorkflow', { payload: this.formWorkflow, force });
    },


    onDeleteSuccessAction(idx) {
      removeActionIfExist(this.formWorkflow.payload.success_actions, idx);
    },

    onDeleteFailureAction(idx) {
      removeActionIfExist(this.formWorkflow.payload.failure_actions, idx);
    },

    onAddActions(event, actions) {
      removeDuplicateNameAction(actions, event.newIndex, this.formWorkflow);
    },

    onAddFailureActions(event) {
      this.onAddActions(event, this.formWorkflow.payload.failure_actions);
    },

    onAddSuccessActions(event) {
      this.onAddActions(event, this.formWorkflow.payload.success_actions);
    },

    async onActionItemClick(item) {
      if (![IQ_TOOL_ACTION_TYPE_DIALOG, IQ_TOOL_ACTION_TYPE_WORKFLOW].includes(item.unit_type)) {
        return;
      }

      if (this.isChanged && this.selectedWorkflow && this.isEditMode) {
        const confirmedDiscard = await confirmDiscardUnsavedChanges(this);
        if (!confirmedDiscard) return;
      }

      if (item.unit_type === IQ_TOOL_ACTION_TYPE_WORKFLOW) {
        return this.editWorkflow(item.id, true);
      }
      return await this.$store.dispatch('routing/routeIqtoolActionItemById', item);
    },

    onSortByName(a, b) {
      const valA = a.name?.toLowerCase() || '';
      const valB = b.name?.toLowerCase() || '';
      if (valA === valB) return 0;
      return valA < valB ? 1 : -1;
    },
  },
};
